'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  stage: 'diagnosis' | 'first-meeting' | 'implementation';
}

interface PreparationChecklistProps {
  isHydrated: boolean;
}

export default function PreparationChecklist({ isHydrated }: PreparationChecklistProps) {
  const [activeStage, setActiveStage] = useState<'diagnosis' | 'first-meeting' | 'implementation'>('diagnosis');
  const [checkedItems, setCheckedItems] = useState<number[]>([]);

  const checklistItems: ChecklistItem[] = [
    {
      id: 1,
      title: 'Přehled příjmů a výdajů',
      description: 'Připravte si přibližný přehled vašich měsíčních příjmů a pravidelných výdajů.',
      stage: 'diagnosis',
    },
    {
      id: 2,
      title: 'Finanční cíle',
      description: 'Zamyslete se nad tím, čeho chcete finančně dosáhnout v krátkodobém i dlouhodobém horizontu.',
      stage: 'diagnosis',
    },
    {
      id: 3,
      title: 'Aktuální výzvy',
      description: 'Identifikujte hlavní finanční výzvy nebo otázky, které vás trápí.',
      stage: 'diagnosis',
    },
    {
      id: 4,
      title: 'Výpisy z účtů',
      description: 'Přineste si výpisy z bankovních účtů za poslední 3 měsíce.',
      stage: 'first-meeting',
    },
    {
      id: 5,
      title: 'Smlouvy a dokumenty',
      description: 'Shromážděte smlouvy o úvěrech, pojištění, investicích a dalších finančních produktech.',
      stage: 'first-meeting',
    },
    {
      id: 6,
      title: 'Daňové přiznání',
      description: 'Pokud máte, přineste poslední daňové přiznání.',
      stage: 'first-meeting',
    },
    {
      id: 7,
      title: 'Seznam otázek',
      description: 'Připravte si seznam otázek, na které chcete znát odpovědi.',
      stage: 'first-meeting',
    },
    {
      id: 8,
      title: 'Potřebné dokumenty',
      description: 'Připravte si dokumenty potřebné pro vyřízení doporučených kroků.',
      stage: 'implementation',
    },
    {
      id: 9,
      title: 'Časový prostor',
      description: 'Vyhraďte si čas na vyřízení jednotlivých kroků podle plánu.',
      stage: 'implementation',
    },
    {
      id: 10,
      title: 'Komunikační kanály',
      description: 'Ujistěte se, že máte přístup k emailu a telefonu pro průběžnou komunikaci.',
      stage: 'implementation',
    },
  ];

  const stages = [
    { id: 'diagnosis', label: 'Online diagnóza', icon: 'ClipboardDocumentCheckIcon' },
    { id: 'first-meeting', label: 'První schůzka', icon: 'UserGroupIcon' },
    { id: 'implementation', label: 'Implementace', icon: 'CheckCircleIcon' },
  ];

  const filteredItems = checklistItems.filter(item => item.stage === activeStage);

  const handleStageChange = (stage: 'diagnosis' | 'first-meeting' | 'implementation') => {
    if (!isHydrated) return;
    setActiveStage(stage);
  };

  const handleItemToggle = (itemId: number) => {
    if (!isHydrated) return;
    setCheckedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl lg:text-4xl font-bold text-primary mb-4 text-center font-headline">
        Jak se připravit
      </h2>
      <p className="text-lg text-text-secondary text-center mb-8">
        Kontrolní seznamy pro jednotlivé fáze procesu
      </p>

      {/* Stage Selector */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        {stages.map((stage) => (
          <button
            key={stage.id}
            onClick={() => handleStageChange(stage.id as any)}
            className={`flex-1 px-6 py-4 rounded-xl font-semibold transition-smooth flex items-center justify-center space-x-3 ${
              isHydrated && activeStage === stage.id
                ? 'bg-primary text-primary-foreground shadow-elevated'
                : 'bg-card text-text-primary hover:bg-surface shadow-card'
            }`}
          >
            <Icon name={stage.icon as any} size={20} className="text-current" />
            <span>{stage.label}</span>
          </button>
        ))}
      </div>

      {/* Checklist Items */}
      <div className="bg-card rounded-xl shadow-card p-6 lg:p-8">
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className={`p-4 rounded-lg border-2 transition-smooth cursor-pointer ${
                isHydrated && checkedItems.includes(item.id)
                  ? 'border-success bg-success/5' :'border-border hover:border-primary/30'
              }`}
              onClick={() => handleItemToggle(item.id)}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-smooth ${
                      isHydrated && checkedItems.includes(item.id)
                        ? 'border-success bg-success' :'border-border'
                    }`}
                  >
                    {isHydrated && checkedItems.includes(item.id) && (
                      <Icon name="CheckIcon" size={16} className="text-white" />
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-primary mb-1">{item.title}</h4>
                  <p className="text-sm text-text-secondary">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Progress */}
        {isHydrated && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-secondary">Dokončeno</span>
              <span className="text-sm font-semibold text-primary">
                {checkedItems.filter(id => filteredItems.some(item => item.id === id)).length} / {filteredItems.length}
              </span>
            </div>
            <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
              <div
                className="h-full bg-success transition-smooth"
                style={{
                  width: `${(checkedItems.filter(id => filteredItems.some(item => item.id === id)).length / filteredItems.length) * 100}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-6 bg-trust rounded-xl border border-border">
        <div className="flex items-start space-x-4">
          <Icon name="LightBulbIcon" size={24} className="text-accent flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-lg font-semibold text-primary mb-2">Tip</h4>
            <p className="text-sm text-text-secondary">
              Nemusíte mít vše připravené dokonale. Tyto seznamy slouží jako vodítko - i částečná příprava výrazně zefektivní naši spolupráci. Pokud něco nemáte, není to problém.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}